import React, { useState } from "react";
import { useLanguage } from "@/App";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const { language, t } = useLanguage();
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email, language });
      toast.success(language === "nl" ? "Succesvol geabonneerd!" : "Subscribed successfully!");
      setEmail("");
    } catch (error) {
      toast.error(language === "nl" ? "Er ging iets mis. Probeer opnieuw." : "Something went wrong. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: language === "nl" ? [
      { label: "Custom Webdesign", href: "#services" },
      { label: "AI Automatisering", href: "#ai-automation" },
      { label: "SEO & Marketing", href: "#extra-services" },
      { label: "Logo & Branding", href: "#extra-services" },
      { label: "App Ontwikkeling", href: "#extra-services" },
      { label: "Hosting & Onderhoud", href: "#maintenance" }
    ] : [
      { label: "Custom Web Design", href: "#services" },
      { label: "AI Automation", href: "#ai-automation" },
      { label: "SEO & Marketing", href: "#extra-services" },
      { label: "Logo & Branding", href: "#extra-services" },
      { label: "App Development", href: "#extra-services" },
      { label: "Hosting & Maintenance", href: "#maintenance" }
    ],
    company: language === "nl" ? [
      { label: "Portfolio", href: "#showcase" },
      { label: "Prijzen", href: "#pricing" },
      { label: "Over Ons", href: "#about" },
      { label: "Add-Ons", href: "#addons" },
      { label: "Resultaten", href: "#why-us" },
      { label: "Contact", href: "#contact" }
    ] : [
      { label: "Portfolio", href: "#showcase" },
      { label: "Pricing", href: "#pricing" },
      { label: "About Us", href: "#about" },
      { label: "Add-Ons", href: "#addons" },
      { label: "Results", href: "#why-us" },
      { label: "Contact", href: "#contact" }
    ]
  };

  return (
    <footer
      data-testid="footer"
      className="bg-[#050505] border-t border-[#FF6B00]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <a
              href="/"
              data-testid="footer-logo"
              className="inline-block mb-6"
            >
              <Logo size="small" />
            </a>
            <p className="text-zinc-400 text-sm leading-relaxed mb-4">
              {t("footer.description")}
            </p>
            <div className="text-zinc-500 text-xs space-y-1">
              <p>Torenallee 101, 5617BR Eindhoven</p>
              <p>KvK: 99681293</p>
              <p>BTW: NL005404414B27</p>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4
              className="text-sm font-bold uppercase tracking-wider text-white mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {t("footer.services")}
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    data-testid={`footer-service-link-${index}`}
                    className="text-zinc-400 text-sm hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4
              className="text-sm font-bold uppercase tracking-wider text-white mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {t("footer.company")}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    data-testid={`footer-company-link-${index}`}
                    className="text-zinc-400 text-sm hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4
              className="text-sm font-bold uppercase tracking-wider text-white mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {t("footer.newsletter")}
            </h4>
            <p className="text-zinc-400 text-sm mb-4">
              {t("footer.newsletterText")}
            </p>
            <form onSubmit={handleSubscribe} className="flex">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.emailPlaceholder")}
                data-testid="footer-newsletter-input"
                className="flex-grow bg-transparent border-b border-white/20 py-3 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-[#FF6B00] transition-colors"
              />
              <button
                type="submit"
                disabled={isSubscribing}
                data-testid="footer-newsletter-button"
                className="bg-[#FF6B00] text-white px-4 py-3 hover:bg-[#FF6B00]/90 transition-all disabled:opacity-50"
              >
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-wrap justify-center gap-6">
          <a href="/algemene-voorwaarden" data-testid="footer-legal-terms" className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors">
            {language === "nl" ? "Algemene Voorwaarden" : "Terms & Conditions"}
          </a>
          <a href="/privacy" data-testid="footer-legal-privacy" className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors">
            {language === "nl" ? "Privacybeleid" : "Privacy Policy"}
          </a>
          <a href="/disclaimer" data-testid="footer-legal-disclaimer" className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors">
            Disclaimer
          </a>
          <a href="/cookies" data-testid="footer-legal-cookies" className="text-zinc-600 text-xs hover:text-zinc-400 transition-colors">
            {language === "nl" ? "Cookiebeleid" : "Cookie Policy"}
          </a>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-zinc-500 text-sm">
            © {currentYear} EG Solutions. {t("footer.rights")}
          </p>
          <div className="flex items-center gap-6">
            <a
              href="#"
              data-testid="footer-social-linkedin"
              className="text-zinc-500 hover:text-white transition-colors"
            >
              LinkedIn
            </a>
            <a
              href="#"
              data-testid="footer-social-instagram"
              className="text-zinc-500 hover:text-white transition-colors"
            >
              Instagram
            </a>
            <a
              href="#"
              data-testid="footer-social-twitter"
              className="text-zinc-500 hover:text-white transition-colors"
            >
              Twitter
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
